// src/pages/TrackCreation/Step1.tsx
import React, { useState, useEffect, useRef } from "react";
import { uploadFileToS3Complete } from "../../../../api/s3";
import { useParams } from "react-router-dom";
import activeRadio from "../../../../assets/images/radio_active.png";
import inactiveRadio from "../../../../assets/images/radio_inactive.png";
import { idToLanguage } from "../../../../utils/idtolanguage";
import { useTranslation } from "react-i18next";
import Button from "../../../../components/ui/Button";
import { getProjects, getProject, getWaypointById } from "../../../../api";
import AudioPlayerRewind from "../../../../components/common/AudioPlayer";
import VideoPlayerRewind from "../../../../components/common/VideoPlayer";
import Dropdown from "../../../../components/common/Dropdown";
import { FileInput } from "../../../../components/common/FileInput";
import HorizontalSeparator from "../../../../components/common/HorizontalSeparator";
import RadioButton from "../../../../components/common/RadioButton";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import { useGlobalState } from "../../../../context/useGlobalState";
import { WaypointAudioProps, RouteParams, DropdownOption } from "../../../../types/screensTypes";
import { useWaypointChangeDetection } from "../../../../hooks/useWaypointChangeDetection";
import { useAddonAccess } from "../../../../hooks/useAddonAccess";
import stepStyles from "../createStep.module.css";
import audioFooter from "../../../../components/common/AudioPlayerFooter.module.css";
import wp from "./WaypointEdit.module.css";

const MAX_VIDEO_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB

const WaypointAudio: React.FC<WaypointAudioProps> = ({ navigateToNext }) => {
    const { data, loading, error } = useGlobalState();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { wptId, language } = useParams<RouteParams>();

    // Check if the VIDEO addon is available for this company
    const { hasAccess: hasVideoAddon } = useAddonAccess("VIDEO");

    // Audio state
    const {
        value: mp3File,
        setValue: setMp3File,
        saveIfChanged: audioSaveIfChanged,
        setOriginalValue: setAudioOriginalValue,
    } = useWaypointChangeDetection<string | null>({
        wptId: wptId || "",
        language,
        initialValue: null,
    });

    // Video state (uses same wptId — saves to the same temporary waypoint record)
    const {
        value: mp4File,
        setValue: setMp4File,
        saveIfChanged: videoSaveIfChanged,
        setOriginalValue: setVideoOriginalValue,
    } = useWaypointChangeDetection<string | null>({
        wptId: wptId || "",
        language,
        initialValue: null,
    });

    // Pending files — uploaded to S3 only on save, not on drop
    const [pendingAudioFile, setPendingAudioFile] = useState<File | null>(null);
    const [pendingVideoFile, setPendingVideoFile] = useState<File | null>(null);
    const [audioPreviewUrl, setAudioPreviewUrl] = useState<string | null>(null);
    const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
    const audioPreviewUrlRef = useRef<string | null>(null);
    const videoPreviewUrlRef = useRef<string | null>(null);

    const [mediaType, setMediaType] = useState<"AUDIO" | "VIDEO">("AUDIO");
    const [saving, setSaving] = useState<boolean>(false);
    const [videoError, setVideoError] = useState<string | null>(null);
    const [isProject, setIsProject] = useState<boolean>(false);
    const [projectData, setProjectData] = useState<any>(null);
    const [projects, setProjects] = useState<DropdownOption[]>([]);
    const [selectedProject, setSelectedProject] = useState<string | null>(null);
    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);
    const [selectedAudioOption, setSelectedAudioOption] = useState<string | null>(null);

    // Fetch projects for the dropdown.
    const fetchProjects = async () => {
        const token = localStorage.getItem("token");
        if (!token) return;
        try {
            const response = await getProjects({ token });
            if (response) {
                const projectsDropdown = response.map((project: any) => ({
                    label: project.name,
                    value: project.id,
                }));
                setProjects(projectsDropdown);
            }
        } catch (err) {
            console.error("Failed to fetch projects:", err);
        }
    };

    const handleAudioSelect = (value: string) => {
        setSelectedAudioOption(value);
        setMp3File(value);
        // Clear any pending file since user chose from project
        if (pendingAudioFile) {
            setPendingAudioFile(null);
            if (audioPreviewUrlRef.current) {
                URL.revokeObjectURL(audioPreviewUrlRef.current);
                audioPreviewUrlRef.current = null;
            }
            setAudioPreviewUrl(null);
        }
    };

    // Fetch a single project by ID.
    const fetchProject = async (id: string) => {
        if (!id) return;
        const token = localStorage.getItem("token");
        if (!token) return;
        const response = await getProject({ token, projectId: id });
        if (!response) {
            console.error("Error fetching project");
        } else {
            setProjectData(response);
        }
    };

    // Get the current audio/video from the waypoint.
    const getWaypoint = async (id: string) => {
        const token = localStorage.getItem("token");
        if (!token) return;
        if (!language) return;
        const waypointData = await getWaypointById({ token, id, language });

        // Resolve mediaType: prefer temporary waypoint when it exists (user may have switched VIDEO ↔ AUDIO)
        const resolvedMediaType: "AUDIO" | "VIDEO" =
            (waypointData?.waypointTemporary as any)?.mediaType === "VIDEO" ||
            (!waypointData?.waypointTemporary && (waypointData?.waypoint as any)?.mediaType === "VIDEO")
                ? "VIDEO"
                : "AUDIO";
        setMediaType(resolvedMediaType);

        if (resolvedMediaType === "VIDEO") {
            // Load video URL
            let loadedVideoUrl: string | null = null;
            if (
                waypointData?.waypointTemporary?.TemporaryWaypointLocalized &&
                waypointData.waypointTemporary.TemporaryWaypointLocalized.length > 0
            ) {
                const videoUrl = waypointData.waypointTemporary.TemporaryWaypointLocalized[0].videoUrl;
                if (videoUrl) {
                    loadedVideoUrl = videoUrl;
                } else if (waypointData.waypoint?.waypointLocalized?.length > 0) {
                    loadedVideoUrl = (waypointData.waypoint.waypointLocalized[0] as any)?.videoUrl || null;
                }
            } else if (waypointData?.waypoint?.waypointLocalized?.length > 0) {
                loadedVideoUrl = (waypointData.waypoint.waypointLocalized[0] as any)?.videoUrl || null;
            }
            setMp4File(loadedVideoUrl);
            setVideoOriginalValue(loadedVideoUrl);
        } else {
            // Load audio URL
            let loadedAudioUrl: string | null = null;
            if (
                waypointData?.waypointTemporary?.TemporaryWaypointLocalized &&
                waypointData.waypointTemporary.TemporaryWaypointLocalized.length > 0
            ) {
                const localized = waypointData.waypointTemporary.TemporaryWaypointLocalized[0];
                const audioUrl = localized.audioUrl;
                if (audioUrl) {
                    loadedAudioUrl = audioUrl;
                } else if (waypointData.waypoint?.waypointLocalized?.length > 0) {
                    loadedAudioUrl = waypointData.waypoint.waypointLocalized[0]?.audiotrack?.file || null;
                }
            } else if (waypointData?.waypoint?.waypointLocalized?.length > 0) {
                loadedAudioUrl = waypointData.waypoint.waypointLocalized[0]?.audiotrack?.file || null;
            }
            setMp3File(loadedAudioUrl);
            setAudioOriginalValue(loadedAudioUrl);
        }
    };

    const handleAudioFileSelected = (files: File | File[]) => {
        const file = Array.isArray(files) ? files[0] : files;
        if (!file) return;
        // Revoke previous preview URL
        if (audioPreviewUrlRef.current) URL.revokeObjectURL(audioPreviewUrlRef.current);
        const previewUrl = URL.createObjectURL(file);
        audioPreviewUrlRef.current = previewUrl;
        setAudioPreviewUrl(previewUrl);
        setPendingAudioFile(file);
    };

    const handleVideoFileSelected = (files: File | File[]) => {
        const file = Array.isArray(files) ? files[0] : files;
        if (!file) return;

        setVideoError(null);

        if (file.size > MAX_VIDEO_SIZE_BYTES) {
            setVideoError(t("videoFileTooLarge") || "Video file exceeds the 20 MB limit.");
            return;
        }

        // Revoke previous preview URL
        if (videoPreviewUrlRef.current) URL.revokeObjectURL(videoPreviewUrlRef.current);
        const previewUrl = URL.createObjectURL(file);
        videoPreviewUrlRef.current = previewUrl;
        setVideoPreviewUrl(previewUrl);
        setPendingVideoFile(file);
    };

    // Handle media type change from dropdown — local UI toggle only.
    // mediaType is persisted alongside the file when the user clicks "continue".
    const handleMediaTypeChange = (value: string) => {
        setMediaType(value as "AUDIO" | "VIDEO");
    };

    // Save the current temporary waypoint with the audio/video.
    // Files are uploaded to S3 here (not on drop) to avoid orphaned files.
    const handleSaveChanges = async (): Promise<void> => {
        if (!wptId || !language) return;
        const token = localStorage.getItem("token");
        if (!token) return;

        setSaving(true);
        try {
            if (mediaType === "VIDEO") {
                if (pendingVideoFile) {
                    // New file dropped — upload to S3 now, then save via change detection
                    const videoUrl = await uploadFileToS3Complete(
                        { file: pendingVideoFile, isPublic: true, isFolder: false, fileSize: pendingVideoFile.size },
                        token,
                    );
                    setPendingVideoFile(null);
                    if (videoPreviewUrlRef.current) {
                        URL.revokeObjectURL(videoPreviewUrlRef.current);
                        videoPreviewUrlRef.current = null;
                    }
                    setVideoPreviewUrl(null);
                    setMp4File(videoUrl);
                    // Force save — state closure still has old value, but we know there's a change
                    await videoSaveIfChanged({ videoUrl, mediaType: "VIDEO" }, () => true);
                    setVideoOriginalValue(videoUrl);
                } else if (mp4File) {
                    await videoSaveIfChanged({ videoUrl: mp4File, mediaType: "VIDEO" });
                } else {
                    return;
                }
            } else {
                if (pendingAudioFile) {
                    const audioUrl = await uploadFileToS3Complete(
                        { file: pendingAudioFile, isPublic: true, isFolder: false },
                        token,
                    );
                    setPendingAudioFile(null);
                    if (audioPreviewUrlRef.current) {
                        URL.revokeObjectURL(audioPreviewUrlRef.current);
                        audioPreviewUrlRef.current = null;
                    }
                    setAudioPreviewUrl(null);
                    setMp3File(audioUrl);
                    await audioSaveIfChanged({ audioUrl, mediaType: "AUDIO" }, () => true);
                    setAudioOriginalValue(audioUrl);
                } else if (mp3File) {
                    await audioSaveIfChanged({ audioUrl: mp3File, mediaType: "AUDIO" });
                } else {
                    return;
                }
            }
            navigateToNext(wptId, language);
        } catch (err) {
            console.error("Save failed:", err);
            if (mediaType === "VIDEO") {
                setVideoError(t("videoUploadFailed") || "Upload failed. Please try again.");
            }
        } finally {
            setSaving(false);
        }
    };

    // Toggle to project mode.
    const handleProjectOptionChange = () => {
        setIsProject(true);
    };

    // Compute formatted language options.
    const languagesAIJsonFormatted: DropdownOption[] =
        data?.languagesAI?.map((lang: any) => ({
            value: lang.code,
            label: lang.label,
        })) || [];

    // Compute audio options from the selected project.
    const filteredAudioOptions: DropdownOption[] =
        projectData?.audioAI
            ?.filter((audio: any) => audio.language === selectedContentLanguage)
            ?.sort((a: any, b: any) => a.position - b.position)
            .map((audio: any) => ({
                value: audio.audioUrl,
                label: audio.name,
            })) || [];

    const mediaTypeOptions: DropdownOption[] = [
        { value: "AUDIO", label: t("audio") },
        { value: "VIDEO", label: t("video") },
    ];

    // Revoke blob preview URLs on unmount
    useEffect(() => {
        return () => {
            if (audioPreviewUrlRef.current) URL.revokeObjectURL(audioPreviewUrlRef.current);
            if (videoPreviewUrlRef.current) URL.revokeObjectURL(videoPreviewUrlRef.current);
        };
    }, []);

    useEffect(() => {
        fetchProjects();
    }, []);

    useEffect(() => {
        if (selectedProject) {
            fetchProject(selectedProject);
        }
    }, [selectedProject]);

    useEffect(() => {
        if (wptId && wptId !== "") {
            getWaypoint(wptId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wptId, language]);

    const hasAudioPlayer = Boolean(audioPreviewUrl || mp3File);
    const hasVideoPlayer = Boolean(videoPreviewUrl || mp4File);

    if (mediaType === "VIDEO") {
        return (
            <div className={`${stepStyles.editScreen} ${wp.screen} ${hasVideoPlayer ? wp.screenWithPlayer : ""}`}>
                <TitleSubtitle
                    title={t("videoEdition") + idToLanguage(language || "", data?.languagesAI, t)}
                    subtitle={""}
                    layout="edit"
                />

                {hasVideoAddon && (
                    <div className={wp.mediaType}>
                        <p className={wp.mediaTypeLabel}>{t("selectMediaType")}</p>
                        <Dropdown
                            label=""
                            options={mediaTypeOptions}
                            value={mediaType}
                            onChange={handleMediaTypeChange}
                            loading={false}
                            error={false}
                            fullWidth
                        />
                    </div>
                )}

                <div className={wp.dropzone}>
                    <FileInput
                        onFileSelected={handleVideoFileSelected}
                        placeholder={
                            t("dragAndDropYourVideoFileHereOrClickToSelectOne") ||
                            "Drag and drop your MP4 video here or click to select"
                        }
                        acceptedFileTypes={{ "video/mp4": [".mp4"] }}
                    />
                </div>

                <p className={wp.secHint}>{t("videoFormatHelptext")}</p>

                {videoError && <p style={{ color: "red", marginTop: "8px" }}>{videoError}</p>}

                {hasVideoPlayer && (
                    <div className={audioFooter.sectionAboveFooter}>
                        <HorizontalSeparator />
                        <VideoPlayerRewind src={videoPreviewUrl || mp4File!} />
                    </div>
                )}

                <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                    <Button
                        variant="primary"
                        shape="pill"
                        className={stepStyles.continueBtn}
                        label={saving ? t("saving") || "Saving..." : t("continue")}
                        disabled={saving || (!mp4File && !pendingVideoFile)}
                        onClick={handleSaveChanges}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className={`${stepStyles.editScreen} ${wp.screen} ${hasAudioPlayer ? wp.screenWithPlayer : ""}`}>
            <TitleSubtitle
                title={t("audioEdition") + idToLanguage(language || "", data?.languagesAI, t)}
                subtitle={""}
                layout="edit"
            />

            {hasVideoAddon && (
                <div className={wp.mediaType}>
                    <p className={wp.mediaTypeLabel}>{t("selectMediaType")}</p>
                    <Dropdown
                        label=""
                        options={mediaTypeOptions}
                        value={mediaType}
                        onChange={handleMediaTypeChange}
                        loading={false}
                        error={false}
                        fullWidth
                    />
                </div>
            )}

            <div className={wp.dropzone}>
                <FileInput
                    onFileSelected={handleAudioFileSelected}
                    placeholder={t("dragAndDropYourAudioFileHereOrClickToSelectOne")}
                    acceptedFileTypes={{
                        "audio/mpeg": [".mp3"],
                        "audio/wav": [".wav"],
                        "audio/ogg": [".ogg"],
                        "audio/aac": [".aac"],
                        "audio/flac": [".flac"],
                        "audio/x-m4a": [".m4a"],
                    }}
                />
            </div>

            <div className={wp.projectHeading}>
                <RadioButton
                    label={t("addFromYourProjects")}
                    checked={isProject}
                    onChange={handleProjectOptionChange}
                    activeImage={activeRadio}
                    inactiveImage={inactiveRadio}
                    center={true}
                />
            </div>

            {isProject && (
                <div className={wp.projectRow}>
                    <Dropdown
                        label=""
                        options={projects}
                        onChange={setSelectedProject}
                        placeholder={t("selectProject")}
                        loading={false}
                        error={false}
                        value={selectedProject}
                    />
                    <Dropdown
                        label=""
                        options={languagesAIJsonFormatted}
                        onChange={setSelectedContentLanguage}
                        loading={loading}
                        error={error ? true : false}
                        placeholder={t("selectALanguage")}
                        value={selectedContentLanguage}
                    />
                    {selectedProject && selectedContentLanguage && (
                        <Dropdown
                            label=""
                            options={filteredAudioOptions}
                            onChange={handleAudioSelect}
                            loading={loading}
                            error={error ? true : false}
                            placeholder={t("selectAnAudio")}
                            value={selectedAudioOption}
                        />
                    )}
                </div>
            )}

            {hasAudioPlayer && (
                <div className={audioFooter.sectionAboveFooter}>
                    <HorizontalSeparator />
                    <AudioPlayerRewind title={""} src={audioPreviewUrl || mp3File!} isAutoPlay={false} />
                </div>
            )}

            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                    label={saving ? t("saving") || "Saving..." : t("continue")}
                    disabled={saving || (!mp3File && !pendingAudioFile)}
                    onClick={handleSaveChanges}
                />
            </div>
        </div>
    );
};

export default WaypointAudio;
